/**
 * 이상한 방 - 규모별 평면도 데이터
 *
 * 손으로 적은 아파트(`DEFAULT_FLOOR_PLAN`)와 밴드 명세 넷(`BAND_PLAN_SPECS`), 그리고
 * 규모 → 평면도 캐시(`getFloorPlan`)가 여기 있다. 데이터 모델은 `floorPlanModel.ts`,
 * 밴드 명세를 평면도로 굽는 규칙은 `bandPlan.ts`에 있으며 이 파일은 둘을 import 만 한다.
 * 역방향은 금지다 - 아래 최상위 상수(`NORTH_EXTERIOR_Z` 등)가 모듈 평가 시점에 모델의
 * 상수를 읽으므로, 모델 쪽이 이 파일을 re-export 하면 TDZ ReferenceError로 터진다.
 *
 * 호출부는 이 파일을 직접 보지 않는다. 배럴 `floorPlan.ts`가
 * `DEFAULT_FLOOR_PLAN`·`getFloorPlan`·`FloorPlanSize`만 내보낸다.
 *
 * 여럿이 판의 평면도는 여기 없다 - 좌석 수의 함수라 표가 아니라 규칙이고, `partyPlan.ts`에 산다.
 *
 * ## `Room.name`은 키다
 *
 * 예전에는 여기에 '거실'·'안방'이 한국어로 박혀 있었다. 데이터라서 통과해 왔지만 M11에서
 * '거실은 OO의 구획' 같은 문장이 화면에 뜨면서 그 한국어가 화면 문구가 됐다. `core`는 `i18n`을
 * import 할 수 없으므로(레이어 방향) 이름은 여기서 **키 문자열**로만 두고, 사람이 읽는 말은
 * 사전의 `room.<id>`가 준다. 그래서 이 파일의 모든 `name`은 자기 `id`와 같은 문자열이다.
 */

import {
  BATH_WINDOW_HEIGHT_CELLS,
  BATH_WINDOW_SILL_CELLS,
  PLAN_HEIGHT_CELLS,
  WINDOW_HEIGHT_CELLS,
  WINDOW_SILL_CELLS,
  buildBandPlan,
  type BandPlanSpec,
} from './bandPlan'
import { OUTER_WALL_CELLS, type FloorPlan } from './floorPlanModel'
import { assertValidFloorPlan } from './floorPlanValidate'

/* --- 기본 평면도(아파트) -------------------------------------------------------- */

/**
 * 기본 평면도의 격자 좌표.
 *
 * 20m × 20m를 세 개의 x 밴드와 세 개의 z 밴드로 자른다. 밴드 사이 한 칸이 곧 내벽이다.
 * `SPINE_*`는 남→북으로 관통하는 척추 벽, `BAND_*`는 동→서로 관통하는 밴드 벽이다.
 *
 * 복도를 세 토막으로 끊은 것이 이 평면도의 핵심이다 -
 * 20m짜리 일자 복도를 두면 한쪽 끝에서 반대쪽 끝까지 다 보여 구획을 나눈 의미가 없다.
 * 토막 사이는 문으로만 이어지므로 시야가 문 폭만큼만 통과한다.
 */
const SPINE_WEST_X = 25
const SPINE_EAST_X = 52
const BAND_SOUTH_Z = 37
const BAND_NORTH_Z = 48
/** 북쪽 밴드에서만 쓰는 내벽(안방 ↔ 욕실). 밴드마다 분할이 달라야 평면이 단조롭지 않다. */
const NORTH_SPLIT_X = 31

const PLAN_SIZE_CELLS = 80

/** 일반 문 폭 4셀 = 1.0m(문틀 포함). 아치형 개구부는 6셀 = 1.5m. */
const DOOR_WIDTH_CELLS = 4
const ARCH_WIDTH_CELLS = 6

const SOUTH_EXTERIOR_Z = 0
const NORTH_EXTERIOR_Z = PLAN_SIZE_CELLS - OUTER_WALL_CELLS
const WEST_EXTERIOR_X = 0
const EAST_EXTERIOR_X = PLAN_SIZE_CELLS - OUTER_WALL_CELLS

/**
 * 20m × 3m × 20m 밀폐 실내. 9개 구획 · 10개 문 · 12개 창.
 *
 * ```
 *  z=79 ┌──────────────┬─────────┬──────────────┐  북
 *       │  안방        │  욕실   │  게스트룸    │
 *  z=48 ├──────┬───────┴──┬──────┴──────────────┤
 *       │ 복도서│ 복도중   │ 복도동              │
 *  z=37 ├──────┼──────────┼─────────────────────┤
 *       │ 서재 │  거실    │  주방               │
 *  z=0  └──────┴──────────┴─────────────────────┘  남
 *      x=0                                     x=79
 * ```
 *
 * 배치 가능 바닥은 5,776셀(361m²)이며, 목표 소품 300~500개를 놓아도 통로가 남는다.
 */
export const DEFAULT_FLOOR_PLAN: FloorPlan = {
  id: 'apartment-01',
  bounds: { sizeX: PLAN_SIZE_CELLS, sizeY: PLAN_HEIGHT_CELLS, sizeZ: PLAN_SIZE_CELLS },
  rooms: [
    {
      id: 'study',
      name: 'study',
      tag: 'living',
      rect: { x: 1, z: 1, sizeX: SPINE_WEST_X - 1, sizeZ: BAND_SOUTH_Z - 1 },
    },
    {
      id: 'living',
      name: 'living',
      tag: 'living',
      rect: {
        x: SPINE_WEST_X + 1,
        z: 1,
        sizeX: SPINE_EAST_X - SPINE_WEST_X - 1,
        sizeZ: BAND_SOUTH_Z - 1,
      },
    },
    {
      id: 'kitchen',
      name: 'kitchen',
      tag: 'kitchen',
      rect: {
        x: SPINE_EAST_X + 1,
        z: 1,
        sizeX: EAST_EXTERIOR_X - SPINE_EAST_X - 1,
        sizeZ: BAND_SOUTH_Z - 1,
      },
    },
    {
      id: 'hall-west',
      name: 'hall-west',
      tag: 'hall',
      rect: {
        x: 1,
        z: BAND_SOUTH_Z + 1,
        sizeX: SPINE_WEST_X - 1,
        sizeZ: BAND_NORTH_Z - BAND_SOUTH_Z - 1,
      },
    },
    {
      id: 'hall-mid',
      name: 'hall-mid',
      tag: 'hall',
      rect: {
        x: SPINE_WEST_X + 1,
        z: BAND_SOUTH_Z + 1,
        sizeX: SPINE_EAST_X - SPINE_WEST_X - 1,
        sizeZ: BAND_NORTH_Z - BAND_SOUTH_Z - 1,
      },
    },
    {
      id: 'hall-east',
      name: 'hall-east',
      tag: 'hall',
      rect: {
        x: SPINE_EAST_X + 1,
        z: BAND_SOUTH_Z + 1,
        sizeX: EAST_EXTERIOR_X - SPINE_EAST_X - 1,
        sizeZ: BAND_NORTH_Z - BAND_SOUTH_Z - 1,
      },
    },
    {
      id: 'bedroom-main',
      name: 'bedroom-main',
      tag: 'bedroom',
      rect: {
        x: 1,
        z: BAND_NORTH_Z + 1,
        sizeX: NORTH_SPLIT_X - 1,
        sizeZ: NORTH_EXTERIOR_Z - BAND_NORTH_Z - 1,
      },
    },
    {
      id: 'bath',
      name: 'bath',
      tag: 'bath',
      rect: {
        x: NORTH_SPLIT_X + 1,
        z: BAND_NORTH_Z + 1,
        sizeX: SPINE_EAST_X - NORTH_SPLIT_X - 1,
        sizeZ: NORTH_EXTERIOR_Z - BAND_NORTH_Z - 1,
      },
    },
    {
      id: 'bedroom-guest',
      name: 'bedroom-guest',
      tag: 'bedroom',
      rect: {
        x: SPINE_EAST_X + 1,
        z: BAND_NORTH_Z + 1,
        sizeX: EAST_EXTERIOR_X - SPINE_EAST_X - 1,
        sizeZ: NORTH_EXTERIOR_Z - BAND_NORTH_Z - 1,
      },
    },
  ],
  doorways: [
    // 남쪽 밴드 → 복도. 문을 각 방의 서로 다른 위치에 둬서 복도에서 방 안이 통째로 보이지 않게 한다.
    { fromRoomId: 'study', toRoomId: 'hall-west', cell: { x: 10, z: BAND_SOUTH_Z }, width: DOOR_WIDTH_CELLS, axis: 'x' },
    { fromRoomId: 'living', toRoomId: 'hall-mid', cell: { x: 34, z: BAND_SOUTH_Z }, width: ARCH_WIDTH_CELLS, axis: 'x' },
    { fromRoomId: 'kitchen', toRoomId: 'hall-east', cell: { x: 62, z: BAND_SOUTH_Z }, width: DOOR_WIDTH_CELLS, axis: 'x' },
    // 남쪽 밴드 내부 연결. 거실-주방은 개방형 아치, 서재는 일반 문.
    { fromRoomId: 'living', toRoomId: 'kitchen', cell: { x: SPINE_EAST_X, z: 8 }, width: ARCH_WIDTH_CELLS, axis: 'z' },
    { fromRoomId: 'study', toRoomId: 'living', cell: { x: SPINE_WEST_X, z: 20 }, width: DOOR_WIDTH_CELLS, axis: 'z' },
    // 복도 토막 사이. 여기가 끊겨 있어야 20m 일자 시야가 생기지 않는다.
    { fromRoomId: 'hall-west', toRoomId: 'hall-mid', cell: { x: SPINE_WEST_X, z: 40 }, width: ARCH_WIDTH_CELLS, axis: 'z' },
    { fromRoomId: 'hall-mid', toRoomId: 'hall-east', cell: { x: SPINE_EAST_X, z: 40 }, width: ARCH_WIDTH_CELLS, axis: 'z' },
    // 복도 → 북쪽 밴드.
    { fromRoomId: 'hall-west', toRoomId: 'bedroom-main', cell: { x: 12, z: BAND_NORTH_Z }, width: DOOR_WIDTH_CELLS, axis: 'x' },
    { fromRoomId: 'hall-mid', toRoomId: 'bath', cell: { x: 38, z: BAND_NORTH_Z }, width: DOOR_WIDTH_CELLS, axis: 'x' },
    { fromRoomId: 'hall-east', toRoomId: 'bedroom-guest', cell: { x: 62, z: BAND_NORTH_Z }, width: DOOR_WIDTH_CELLS, axis: 'x' },
  ],
  windows: [
    { id: 'win-study-s', roomId: 'study', cell: { x: 6, z: SOUTH_EXTERIOR_Z }, width: 12, axis: 'x', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-study-w', roomId: 'study', cell: { x: WEST_EXTERIOR_X, z: 8 }, width: 12, axis: 'z', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-living-s', roomId: 'living', cell: { x: 32, z: SOUTH_EXTERIOR_Z }, width: 14, axis: 'x', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-kitchen-s', roomId: 'kitchen', cell: { x: 60, z: SOUTH_EXTERIOR_Z }, width: 12, axis: 'x', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-kitchen-e', roomId: 'kitchen', cell: { x: EAST_EXTERIOR_X, z: 20 }, width: 10, axis: 'z', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-hall-w', roomId: 'hall-west', cell: { x: WEST_EXTERIOR_X, z: 40 }, width: 6, axis: 'z', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-hall-e', roomId: 'hall-east', cell: { x: EAST_EXTERIOR_X, z: 40 }, width: 6, axis: 'z', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-bedmain-n', roomId: 'bedroom-main', cell: { x: 8, z: NORTH_EXTERIOR_Z }, width: 14, axis: 'x', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-bedmain-w', roomId: 'bedroom-main', cell: { x: WEST_EXTERIOR_X, z: 58 }, width: 12, axis: 'z', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-bath-n', roomId: 'bath', cell: { x: 38, z: NORTH_EXTERIOR_Z }, width: 8, axis: 'x', sillCell: BATH_WINDOW_SILL_CELLS, heightCells: BATH_WINDOW_HEIGHT_CELLS },
    { id: 'win-bedguest-n', roomId: 'bedroom-guest', cell: { x: 60, z: NORTH_EXTERIOR_Z }, width: 14, axis: 'x', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
    { id: 'win-bedguest-e', roomId: 'bedroom-guest', cell: { x: EAST_EXTERIOR_X, z: 58 }, width: 12, axis: 'z', sillCell: WINDOW_SILL_CELLS, heightCells: WINDOW_HEIGHT_CELLS },
  ],
}

/* --- 크기별 평면도 -------------------------------------------------------------- */

/**
 * 평면도 규모.
 *
 * `game/levels.ts`의 `PlanSize`와 **같은 문자열 유니온**이며, 구조적 타이핑 덕분에
 * `getFloorPlan(level.planSize)`가 어댑터 없이 그대로 통과한다. 이름을 여기서 한 번 더
 * 선언하는 이유는 방향이다 - 공간 어휘는 core의 것이고, core가 game을 import 하는 순간
 * 평면도가 게임 규칙에 매인다. 한쪽에만 규모가 추가되면 호출부에서 컴파일 에러가 나므로
 * 어긋남은 조용히 넘어가지 않는다.
 */
export type FloorPlanSize = 'room' | 'two-rooms' | 'small-house' | 'house' | 'apartment'

/* --- 평면도 표 ------------------------------------------------------------------ */

/**
 * 규모별 밴드 명세. 아파트만 손으로 적은 `DEFAULT_FLOOR_PLAN`을 그대로 쓴다 -
 * 이미 검증된 데이터를 규칙으로 다시 만들면 이득 없이 회귀 위험만 생긴다.
 *
 * 구획 수는 규모의 정의이므로 여기서 눈으로 셀 수 있어야 한다:
 * 방 하나(1) · 방 둘 + 복도(3) · 작은 집(4) · 집(6) · 아파트(9).
 */
const BAND_PLAN_SPECS: Readonly<Record<Exclude<FloorPlanSize, 'apartment'>, BandPlanSpec>> = {
  /**
   * 24 × 24셀 = 6m × 6m 원룸. 튜토리얼 1·2단계가 쓴다.
   *
   * 문이 없는 것이 이 평면도의 요점이다 - 1·2단계는 **동사 하나를 배우는 자리**이므로
   * 방을 옮겨 다니는 동작이 끼어들면 안 된다. 화면 하나에 전부 들어온다.
   */
  room: {
    id: 'room-01',
    xBands: [22],
    zBands: [22],
    zones: [{ id: 'room', name: 'room', tag: 'living', col: 0, row: 0 }],
    doors: [],
  },

  /**
   * 40 × 32셀 = 10m × 8m. 방 둘을 **짧은 복도로만** 잇는다.
   *
   * 두 방을 직접 잇지 않은 것이 의도다. 3·4단계의 힌트가 "한 방을 다 본 뒤 옆방으로"인데,
   * 두 방이 서로 보이면 그 조언이 성립하지 않는다 - 시야가 끊겨야 기억이 방 단위로 묶인다.
   */
  'two-rooms': {
    id: 'two-rooms-01',
    xBands: [18, 19],
    zBands: [21, 8],
    zones: [
      { id: 'living', name: 'living', tag: 'living', col: 0, row: 0 },
      { id: 'bedroom', name: 'bedroom', tag: 'bedroom', col: 1, row: 0 },
      { id: 'hall', name: 'hall', tag: 'hall', col: 0, row: 1, colSpan: 2 },
    ],
    doors: [
      { from: 'living', to: 'hall', width: DOOR_WIDTH_CELLS },
      { from: 'bedroom', to: 'hall', width: DOOR_WIDTH_CELLS },
    ],
  },

  /** 56 × 48셀 = 14m × 12m. 네 구획이 나무 모양으로 이어진다(욕실은 침실 안쪽). */
  'small-house': {
    id: 'small-house-01',
    xBands: [30, 23],
    zBands: [26, 19],
    zones: [
      { id: 'living', name: 'living', tag: 'living', col: 0, row: 0 },
      { id: 'kitchen', name: 'kitchen', tag: 'kitchen', col: 1, row: 0 },
      { id: 'bedroom', name: 'bedroom', tag: 'bedroom', col: 0, row: 1 },
      { id: 'bath', name: 'bath', tag: 'bath', col: 1, row: 1 },
    ],
    doors: [
      { from: 'living', to: 'kitchen', width: ARCH_WIDTH_CELLS },
      { from: 'living', to: 'bedroom', width: DOOR_WIDTH_CELLS },
      { from: 'bedroom', to: 'bath', width: DOOR_WIDTH_CELLS },
    ],
  },

  /**
   * 64 × 64셀 = 16m × 16m. 아파트와 같은 **복도 중심** 구조를 여섯 구획으로 줄인 것.
   *
   * 9단계에서 처음 아파트를 만났을 때 공간 구조가 낯설면 안 되므로,
   * 7·8단계에서 같은 문법(남쪽 생활공간 → 복도 → 북쪽 사적공간)을 미리 익히게 한다.
   */
  house: {
    id: 'house-01',
    xBands: [21, 19, 20],
    zBands: [26, 8, 26],
    zones: [
      { id: 'living', name: 'living', tag: 'living', col: 0, row: 0, colSpan: 2 },
      { id: 'kitchen', name: 'kitchen', tag: 'kitchen', col: 2, row: 0 },
      { id: 'hall', name: 'hall', tag: 'hall', col: 0, row: 1, colSpan: 3 },
      { id: 'bedroom-main', name: 'bedroom-main', tag: 'bedroom', col: 0, row: 2 },
      { id: 'bath', name: 'bath', tag: 'bath', col: 1, row: 2 },
      { id: 'bedroom-guest', name: 'bedroom-guest', tag: 'bedroom', col: 2, row: 2 },
    ],
    doors: [
      { from: 'living', to: 'kitchen', width: ARCH_WIDTH_CELLS },
      { from: 'living', to: 'hall', width: ARCH_WIDTH_CELLS },
      { from: 'kitchen', to: 'hall', width: DOOR_WIDTH_CELLS },
      { from: 'hall', to: 'bedroom-main', width: DOOR_WIDTH_CELLS },
      { from: 'hall', to: 'bath', width: DOOR_WIDTH_CELLS },
      { from: 'hall', to: 'bedroom-guest', width: DOOR_WIDTH_CELLS },
    ],
  },
}

/**
 * 규모별 평면도 캐시.
 *
 * 평면도는 **불변 데이터**이므로 한 번 만들면 계속 쓴다. 단계를 되풀이할 때마다 다시 굽는 것은
 * 낭비일 뿐 아니라, 셸·조명·생성기가 서로 다른 인스턴스를 들 여지를 만든다
 * (`cutaway`가 셸의 평면도 id를 대조하는 이유와 같은 종류의 사고다).
 */
const planCache = new Map<FloorPlanSize, FloorPlan>()

/**
 * 규모에 해당하는 평면도. 처음 요청될 때 한 번 굽고 **즉시 검증한다**.
 *
 * 검증을 여기 둔 이유: 밴드 명세는 손으로 적는 데이터이고, 어긋난 평면도는 화면에서
 * "왜 이 방에만 못 들어가지"로 나타나 원인 추적이 거의 불가능하다.
 * 부트스트랩에서 터지면 `main.ts`의 catch가 받아 `app/boot.ts`의 `reportFatalError`가
 * 실패 배너로 문장을 그대로 보여 준다.
 */
export function getFloorPlan(size: FloorPlanSize): FloorPlan {
  const cached = planCache.get(size)
  if (cached !== undefined) return cached

  const plan = size === 'apartment' ? DEFAULT_FLOOR_PLAN : buildBandPlan(BAND_PLAN_SPECS[size])
  assertValidFloorPlan(plan)
  planCache.set(size, plan)

  return plan
}
